<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';
import UploadAPI from 'dashboard/api/upload';
import { formatDate, TYPE_LABELS, TYPE_COLORS } from './useMonitorHelpers';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
  fromDate: {
    type: Number,
    required: true,
  },
  toDate: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const mediaList = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const filterType = ref('');
const selectedMedia = ref(null);

// Publish form
const showPublishForm = ref(false);
const publishCaption = ref('');
const publishMediaUrl = ref('');
const publishMediaType = ref('IMAGE');
const publishSourceMode = ref('file'); // 'file' or 'url'
const publishFile = ref(null);
const publishFilePreview = ref(null);
const isUploading = ref(false);
const isPublishing = ref(false);
const publishError = ref('');
const publishSuccess = ref('');
const fileInputRef = ref(null);

// Delete
const confirmDeleteId = ref(null);
const isDeleting = ref(false);
const deleteError = ref('');

const MEDIA_TYPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'image', label: 'Imagens' },
  { value: 'video', label: 'Vídeos' },
  { value: 'carousel_album', label: 'Carrosséis' },
  { value: 'reel', label: 'Reels' },
  { value: 'story', label: 'Stories' },
];

const PUBLISH_TYPE_OPTIONS = [
  { value: 'IMAGE', label: 'Imagem' },
  { value: 'VIDEO', label: 'Vídeo' },
  { value: 'REELS', label: 'Reels' },
];

const VIDEO_TYPES = ['video', 'story', 'reel'];

function isVideoType(type) {
  return VIDEO_TYPES.includes(type);
}

const hasInbox = computed(() => !!props.selectedInbox);

async function fetchMedia(page = 1) {
  if (!hasInbox.value) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getMediaList({
      inboxId: props.selectedInbox.id,
      page,
      mediaType: filterType.value || undefined,
      from: props.fromDate,
      to: props.toDate,
    });
    mediaList.value = response.data.records;
    totalPages.value = response.data.total_pages;
    currentPage.value = response.data.current_page;
    totalCount.value = response.data.total_count;
  } catch {
    mediaList.value = [];
  } finally {
    isLoading.value = false;
  }
}

function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    fetchMedia(page);
  }
}

function openDetail(media) {
  selectedMedia.value = media;
}

function closeDetail() {
  selectedMedia.value = null;
}

function confirmDelete(mediaId) {
  confirmDeleteId.value = mediaId;
  deleteError.value = '';
}

function cancelDelete() {
  confirmDeleteId.value = null;
}

async function deleteMedia() {
  if (!confirmDeleteId.value) return;
  isDeleting.value = true;
  deleteError.value = '';
  try {
    await InstagramMediaReportsAPI.deleteMedia({
      inboxId: props.selectedInbox.id,
      mediaId: confirmDeleteId.value,
    });
    confirmDeleteId.value = null;
    selectedMedia.value = null;
    fetchMedia(currentPage.value);
  } catch (err) {
    deleteError.value =
      err.response?.data?.error || 'Erro ao excluir publicação';
  } finally {
    isDeleting.value = false;
  }
}

const isSavingMedia = ref(null);
const isGeneratingRepostLink = ref(null);

async function saveMediaToStorage(media) {
  isSavingMedia.value = media.id;
  try {
    const response = await InstagramMediaReportsAPI.saveMedia({
      inboxId: props.selectedInbox.id,
      mediaId: media.id,
    });
    const updated = response.data;
    const idx = mediaList.value.findIndex(m => m.id === media.id);
    if (idx >= 0) mediaList.value[idx] = updated;
    if (selectedMedia.value?.id === media.id) selectedMedia.value = updated;
  } catch (err) {
    const msg = err.response?.data?.error || 'Erro ao salvar mídia';
    window.alert(msg);
  } finally {
    isSavingMedia.value = null;
  }
}

async function generateRepostLink(media) {
  isGeneratingRepostLink.value = media.id;
  try {
    const response = await InstagramMediaReportsAPI.generateRepostLink({
      inboxId: props.selectedInbox.id,
      mediaId: media.id,
    });
    const url = response.data.url;
    await navigator.clipboard.writeText(url);
    window.alert(`Link de repost copiado!\n\n${url}`);
  } catch (err) {
    const msg = err.response?.data?.error || 'Erro ao gerar link';
    window.alert(msg);
  } finally {
    isGeneratingRepostLink.value = null;
  }
}

function clearFileSelection() {
  if (publishFilePreview.value) {
    URL.revokeObjectURL(publishFilePreview.value);
  }
  publishFile.value = null;
  publishFilePreview.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
}

function togglePublishForm() {
  showPublishForm.value = !showPublishForm.value;
  publishError.value = '';
  publishSuccess.value = '';
  clearFileSelection();
}

function onFileSelected(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  publishFile.value = file;
  publishFilePreview.value = URL.createObjectURL(file);
}

function triggerFileInput() {
  fileInputRef.value?.click();
}

function formatFileSize(bytes) {
  return (bytes / 1024 / 1024).toFixed(2);
}

const fileAccept = computed(() => {
  if (publishMediaType.value === 'IMAGE') return 'image/jpeg,image/png';
  return 'video/mp4,video/quicktime';
});

async function publishPost() {
  let mediaUrl = publishMediaUrl.value;

  // If file mode, upload first
  if (publishSourceMode.value === 'file') {
    if (!publishFile.value) {
      publishError.value = t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_SELECT_FILE');
      return;
    }
    isUploading.value = true;
    publishError.value = '';
    publishSuccess.value = '';
    try {
      const uploadResp = await UploadAPI.uploadFile(publishFile.value);
      mediaUrl = uploadResp.data.file_url;
    } catch {
      publishError.value = t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_UPLOAD_ERROR');
      isUploading.value = false;
      return;
    }
    isUploading.value = false;
  } else if (!mediaUrl) {
    publishError.value = t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_ENTER_URL');
    return;
  }

  isPublishing.value = true;
  publishError.value = '';
  publishSuccess.value = '';
  try {
    await InstagramMediaReportsAPI.publishMedia({
      inboxId: props.selectedInbox.id,
      mediaUrl,
      caption: publishCaption.value,
      mediaType: publishMediaType.value,
    });
    publishSuccess.value = t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_SUCCESS');
    publishCaption.value = '';
    publishMediaUrl.value = '';
    clearFileSelection();
    fetchMedia(1);
  } catch (err) {
    publishError.value =
      err.response?.data?.error || t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_ERROR');
  } finally {
    isPublishing.value = false;
  }
}

watch(
  () => [props.selectedInbox, props.fromDate, props.toDate, filterType.value],
  () => fetchMedia(1),
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- Header with actions -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLICATIONS_TITLE') }}
        </h3>
        <span v-if="totalCount" class="text-sm text-n-slate-11">
          ({{ totalCount }}
          {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLICATIONS_COUNT') }})
        </span>
      </div>
      <div class="flex items-center gap-3">
        <!-- Filter by type -->
        <select
          v-model="filterType"
          class="text-sm rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-n-slate-12"
        >
          <option
            v-for="opt in MEDIA_TYPE_OPTIONS"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <!-- Publish button -->
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors"
          @click="togglePublishForm"
        >
          {{
            showPublishForm
              ? t('INSTAGRAM_MEDIA_REPORTS.CANCEL')
              : t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_NEW')
          }}
        </button>
      </div>
    </div>

    <!-- Publish Form -->
    <div
      v-if="showPublishForm"
      class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6"
    >
      <h4 class="text-base font-semibold text-n-slate-12 mb-3">
        {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_FORM_TITLE') }}
      </h4>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <!-- Media type -->
        <div>
          <label class="block text-sm text-n-slate-11 mb-1">
            {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_TYPE') }}
          </label>
          <select
            v-model="publishMediaType"
            class="w-full text-sm rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-n-slate-12"
          >
            <option
              v-for="opt in PUBLISH_TYPE_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <!-- Source mode toggle -->
        <div>
          <label class="block text-sm text-n-slate-11 mb-1">
            {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_SOURCE') }}
          </label>
          <div class="flex rounded-lg border border-n-weak overflow-hidden">
            <button
              class="flex-1 px-3 py-2 text-sm font-medium transition-colors"
              :class="
                publishSourceMode === 'file'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-solid-2 text-n-slate-11 hover:text-n-slate-12'
              "
              @click="publishSourceMode = 'file'"
            >
              {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_SOURCE_FILE') }}
            </button>
            <button
              class="flex-1 px-3 py-2 text-sm font-medium transition-colors"
              :class="
                publishSourceMode === 'url'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-solid-2 text-n-slate-11 hover:text-n-slate-12'
              "
              @click="publishSourceMode = 'url'"
            >
              {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_SOURCE_URL') }}
            </button>
          </div>
        </div>
        <!-- URL input (when url mode) -->
        <div v-if="publishSourceMode === 'url'">
          <label class="block text-sm text-n-slate-11 mb-1">
            {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_URL') }}
          </label>
          <input
            v-model="publishMediaUrl"
            type="text"
            class="w-full text-sm rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-n-slate-12"
            :placeholder="t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_URL_PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- File upload area (when file mode) -->
      <div v-if="publishSourceMode === 'file'" class="mb-4">
        <input
          ref="fileInputRef"
          type="file"
          :accept="fileAccept"
          class="hidden"
          @change="onFileSelected"
        />
        <div
          v-if="!publishFile"
          class="border-2 border-dashed border-n-weak rounded-lg p-8 text-center cursor-pointer hover:border-n-blue-7 transition-colors"
          @click="triggerFileInput"
        >
          <p class="text-sm text-n-slate-11 mb-1">
            {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_DROP_AREA') }}
          </p>
          <p class="text-xs text-n-slate-9">
            {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_FILE_HINT') }}
          </p>
        </div>
        <div
          v-else
          class="flex items-center gap-4 p-3 rounded-lg border border-n-weak bg-n-alpha-1"
        >
          <img
            v-if="publishFilePreview && publishMediaType === 'IMAGE'"
            :src="publishFilePreview"
            class="w-20 h-20 object-cover rounded-lg"
          />
          <video
            v-else-if="publishFilePreview"
            :src="publishFilePreview"
            class="w-20 h-20 object-cover rounded-lg"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm text-n-slate-12 truncate">
              {{ publishFile.name }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_FILE_SIZE', {
                  size: formatFileSize(publishFile.size),
                })
              }}
            </p>
          </div>
          <button
            class="px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1"
            @click="clearFileSelection"
          >
            {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_CHANGE_FILE') }}
          </button>
        </div>
      </div>
      <div class="mb-4">
        <label class="block text-sm text-n-slate-11 mb-1">
          {{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_CAPTION') }}
        </label>
        <textarea
          v-model="publishCaption"
          rows="3"
          class="w-full text-sm rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-n-slate-12 resize-none"
          :placeholder="
            t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_CAPTION_PLACEHOLDER')
          "
        />
      </div>
      <div class="flex items-center gap-3">
        <button
          :disabled="isPublishing || isUploading"
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50"
          @click="publishPost"
        >
          <Spinner v-if="isPublishing || isUploading" size="tiny" />
          <span v-else>{{ t('INSTAGRAM_MEDIA_REPORTS.PUBLISH_BUTTON') }}</span>
        </button>
        <span v-if="publishError" class="text-sm text-n-ruby-11">
          {{ publishError }}
        </span>
        <span v-if="publishSuccess" class="text-sm text-n-teal-11">
          {{ publishSuccess }}
        </span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <!-- Gallery Grid -->
    <template v-else>
      <div
        v-if="mediaList.length"
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6"
      >
        <div
          v-for="media in mediaList"
          :key="media.id"
          class="group relative bg-n-solid-2 rounded-lg border border-n-weak overflow-hidden cursor-pointer hover:border-n-blue-7 transition-colors"
          @click="openDetail(media)"
        >
          <div class="aspect-square bg-n-slate-3">
            <video
              v-if="isVideoType(media.media_type) && (media.media_url || media.saved_file_url)"
              :src="media.saved_file_url || media.media_url"
              class="w-full h-full object-cover"
              muted
              preload="metadata"
              @click.stop="openDetail(media)"
            />
            <img
              v-else-if="media.thumbnail_url"
              :src="media.thumbnail_url"
              class="w-full h-full object-cover"
              loading="lazy"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center text-n-slate-9"
            >
              <span class="text-xs">{{
                TYPE_LABELS[media.media_type] || media.media_type
              }}</span>
            </div>
            <!-- Play icon overlay for videos -->
            <div
              v-if="isVideoType(media.media_type)"
              class="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div class="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                <svg class="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
              </div>
            </div>
          </div>
          <!-- Overlay with stats -->
          <div
            class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 text-white"
          >
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium">{{ media.like_count }}</span>
              <span class="text-sm font-medium">{{ media.comments_count }}</span>
            </div>
            <div class="flex gap-1.5">
              <button
                class="px-2 py-1 text-xs rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 text-white"
                @click.stop="generateRepostLink(media)"
              >
                {{ isGeneratingRepostLink === media.id ? '...' : 'Repost' }}
              </button>
              <button
                v-if="!media.media_saved"
                class="px-2 py-1 text-xs rounded-lg bg-teal-600 text-white hover:bg-teal-700"
                @click.stop="saveMediaToStorage(media)"
              >
                {{ isSavingMedia === media.id ? '...' : 'Salvar' }}
              </button>
              <span
                v-else
                class="px-2 py-1 text-xs rounded-lg bg-emerald-700 text-white"
              >
                Salvo
              </span>
            </div>
          </div>
          <!-- Type badge -->
          <span
            class="absolute top-1 right-1 text-xs px-1.5 py-0.5 rounded-full text-white"
            :class="[TYPE_COLORS[media.media_type] || 'bg-n-blue-9']"
          >
            {{ TYPE_LABELS[media.media_type] || media.media_type }}
          </span>
          <!-- Saved badge -->
          <span
            v-if="media.media_saved"
            class="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white"
          >
            Salvo
          </span>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="flex items-center justify-center py-12 text-n-slate-11"
      >
        {{ t('INSTAGRAM_MEDIA_REPORTS.NO_DATA') }}
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="flex items-center justify-center gap-2">
        <button
          :disabled="currentPage <= 1"
          class="px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
          @click="goToPage(currentPage - 1)"
        >
          {{ t('INSTAGRAM_MEDIA_REPORTS.PREV_PAGE') }}
        </button>
        <span class="text-sm text-n-slate-11">
          {{ currentPage }} / {{ totalPages }}
        </span>
        <button
          :disabled="currentPage >= totalPages"
          class="px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
          @click="goToPage(currentPage + 1)"
        >
          {{ t('INSTAGRAM_MEDIA_REPORTS.NEXT_PAGE') }}
        </button>
      </div>
    </template>

    <!-- Detail Modal -->
    <teleport to="body">
      <div
        v-if="selectedMedia"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        @click.self="closeDetail"
      >
        <div
          class="bg-n-solid-1 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        >
          <!-- Media preview -->
          <div class="relative">
            <video
              v-if="isVideoType(selectedMedia.media_type) && (selectedMedia.media_url || selectedMedia.saved_file_url)"
              :src="selectedMedia.saved_file_url || selectedMedia.media_url"
              class="w-full max-h-96 object-contain bg-n-slate-3 rounded-t-xl"
              controls
              preload="metadata"
            />
            <img
              v-else-if="selectedMedia.thumbnail_url"
              :src="selectedMedia.thumbnail_url"
              class="w-full max-h-96 object-contain bg-n-slate-3 rounded-t-xl"
            />
            <button
              class="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 before:content-['\00D7']"
              @click="closeDetail"
            />
          </div>

          <div class="p-5">
            <!-- Type + date -->
            <div class="flex items-center gap-3 mb-3">
              <span
                class="text-xs px-2 py-0.5 rounded-full text-white"
                :class="[
                  TYPE_COLORS[selectedMedia.media_type] || 'bg-n-blue-9',
                ]"
              >
                {{
                  TYPE_LABELS[selectedMedia.media_type] ||
                  selectedMedia.media_type
                }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{
                  selectedMedia.timestamp
                    ? formatDate(selectedMedia.timestamp)
                    : ''
                }}
              </span>
            </div>

            <!-- Caption -->
            <p
              v-if="selectedMedia.caption"
              class="text-sm text-n-slate-12 mb-4 whitespace-pre-wrap"
            >
              {{ selectedMedia.caption }}
            </p>
            <p v-else class="text-sm text-n-slate-9 mb-4 italic">
              {{ t('INSTAGRAM_MEDIA_REPORTS.NO_CAPTION') }}
            </p>

            <!-- Stats -->
            <div class="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
              <div class="text-center">
                <p class="text-lg font-semibold text-n-pink-11">
                  {{ selectedMedia.like_count }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ t('INSTAGRAM_MEDIA_REPORTS.CHART_LIKES') }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-lg font-semibold text-n-blue-11">
                  {{ selectedMedia.comments_count }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ t('INSTAGRAM_MEDIA_REPORTS.CHART_COMMENTS') }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedMedia.impressions }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ t('INSTAGRAM_MEDIA_REPORTS.IMPRESSIONS') }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedMedia.reach }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ t('INSTAGRAM_MEDIA_REPORTS.REACH') }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedMedia.saved }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ t('INSTAGRAM_MEDIA_REPORTS.MEDIA_SAVED') }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedMedia.shares }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ t('INSTAGRAM_MEDIA_REPORTS.MEDIA_SHARES') }}
                </p>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-3 border-t border-n-weak pt-4">
              <a
                v-if="selectedMedia.permalink"
                :href="selectedMedia.permalink"
                target="_blank"
                rel="noopener noreferrer"
                class="px-4 py-2 text-sm font-medium rounded-lg border border-n-weak bg-n-solid-2 text-n-blue-11 hover:bg-n-alpha-1 transition-colors"
              >
                {{ t('INSTAGRAM_MEDIA_REPORTS.OPEN_LINK') }}
              </a>
              <button
                :disabled="isGeneratingRepostLink === selectedMedia.id"
                class="px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 transition-colors"
                @click="generateRepostLink(selectedMedia)"
              >
                {{ isGeneratingRepostLink === selectedMedia.id ? 'Gerando...' : 'Link Repost' }}
              </button>
              <button
                v-if="!selectedMedia.media_saved"
                :disabled="isSavingMedia === selectedMedia.id"
                class="px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
                @click="saveMediaToStorage(selectedMedia)"
              >
                {{ isSavingMedia === selectedMedia.id ? 'Salvando...' : 'Salvar na Mídia' }}
              </button>
              <a
                v-if="selectedMedia.media_saved && selectedMedia.saved_file_url"
                :href="selectedMedia.saved_file_url"
                target="_blank"
                class="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                Mídia Salva - Abrir
              </a>
              <button
                class="px-4 py-2 text-sm font-medium rounded-lg bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-colors"
                @click="confirmDelete(selectedMedia.id)"
              >
                {{ t('INSTAGRAM_MEDIA_REPORTS.DELETE_POST') }}
              </button>
            </div>

            <!-- Delete confirmation -->
            <div
              v-if="confirmDeleteId === selectedMedia.id"
              class="mt-3 p-3 rounded-lg border border-n-ruby-7 bg-n-ruby-2"
            >
              <p class="text-sm text-n-ruby-11 mb-2">
                {{ t('INSTAGRAM_MEDIA_REPORTS.DELETE_CONFIRM') }}
              </p>
              <div class="flex gap-2">
                <button
                  :disabled="isDeleting"
                  class="px-3 py-1.5 text-sm font-medium rounded-lg bg-n-ruby-9 text-white hover:bg-n-ruby-10 disabled:opacity-50"
                  @click="deleteMedia"
                >
                  <Spinner v-if="isDeleting" size="tiny" />
                  <span v-else>{{
                    t('INSTAGRAM_MEDIA_REPORTS.DELETE_CONFIRM_YES')
                  }}</span>
                </button>
                <button
                  class="px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1"
                  @click="cancelDelete"
                >
                  {{ t('INSTAGRAM_MEDIA_REPORTS.CANCEL') }}
                </button>
              </div>
              <p v-if="deleteError" class="text-sm text-n-ruby-11 mt-2">
                {{ deleteError }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
